import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchVideoResourceMappings,
  fetchVideos,
  indexVideoResourceMappings,
  mappedDispatchSlug,
  type Video,
  type VideoResourceMapping,
} from "@/lib/cms";
import VideoPlayerModal from "@/components/cms/VideoPlayerModal";

const VIDEO_FETCH_COUNT = 6;

function formatMonthYear(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function buildEmbedUrl(videoId: string): string {
  const params = new URLSearchParams({
    autoplay: "1",
    rel: "0",
    modestbranding: "1",
  });
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?${params}`;
}

export default function FeaturedThisWeek() {
  const [videos, setVideos] = useState<Video[]>([]);
  const [mappings, setMappings] = useState<VideoResourceMapping[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [featuredPlaying, setFeaturedPlaying] = useState(false);
  const [activeVideo, setActiveVideo] = useState<Video | null>(null);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      fetchVideos({ limit: VIDEO_FETCH_COUNT, sort: "-publishedAt" }),
      fetchVideoResourceMappings().catch(() => ({ docs: [] as VideoResourceMapping[] })),
    ])
      .then(([videosRes, mappingsRes]) => {
        if (cancelled) return;
        setVideos(videosRes.docs);
        setMappings(mappingsRes.docs);
        setStatus("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[FeaturedThisWeek] fetch failed", err);
        setStatus("error");
        setErrorMessage(
          err instanceof Error ? err.message : "Failed to load videos",
        );
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const mappingIndex = useMemo(
    () => indexVideoResourceMappings(mappings),
    [mappings],
  );

  const featured = videos[0];
  const featuredMapping = featured
    ? (mappingIndex.get(featured.youtubeVideoId) ?? null)
    : null;
  const featuredDispatchSlug = featuredMapping
    ? mappedDispatchSlug(featuredMapping)
    : null;

  const more = videos.slice(1, 6);

  return (
    <section
      className="w-full bg-black py-14 font-fira text-white sm:py-20"
      aria-labelledby="featured-this-week-heading"
    >
      <div className="mx-auto w-[90%] md:w-[80%]">
        <div className="mb-8 flex flex-col gap-6 md:mb-10 md:flex-row md:items-start md:justify-between md:gap-8">
          <header className="max-w-2xl text-left">
            <p className="font-fira mb-3 text-xs font-normal uppercase tracking-[1.75px] text-[#FFFFFF] md:text-sm">
              Featured this week
            </p>
            <h2
              id="featured-this-week-heading"
              className="font-fira-condensed text-[30px] md:font-semibold font-medium leading-tight tracking-tight text-white md:text-[42px]"
            >
              Watch. Then apply.
            </h2>
          </header>

          {/* Desktop only — header-row "Explore more". On mobile the
              duplicate at the section bottom is shown instead. */}
          <div className="hidden shrink-0 md:block md:pt-1">
            <a
              href="/all-resources"
              className="inline-flex items-center justify-center rounded-lg border border-[#FFFFFF4D] bg-transparent px-8 py-3 text-center text-sm font-medium text-white transition hover:bg-white/5"
            >
              Explore more
            </a>
          </div>
        </div>

        {status === "error" && errorMessage ? (
          <p className="mb-6 text-[14px] leading-relaxed text-red-300/90">
            {errorMessage}
          </p>
        ) : null}

        {status === "loading" && (
          <p className="text-[14px] text-[#FFFFFF85]">Loading videos…</p>
        )}

        {status === "ready" && featured && (
          <div className="grid gap-10 md:grid-cols-5 md:gap-8 lg:gap-12">
            {/* Featured — left */}
            <div className="flex min-w-0 flex-col gap-4 md:col-span-3">
              <div className="relative w-full max-w-[550px] md:max-w-none">
                {featured.thumbnailUrl && !featuredPlaying ? (
                  <img
                    src={featured.thumbnailUrl}
                    alt=""
                    aria-hidden
                    className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-full w-full max-w-none -translate-x-1/2 -translate-y-1/2 object-cover opacity-[0.55] blur-[44px] saturate-[1.12] md:h-[132%] md:w-[132%] md:blur-[52px]"
                  />
                ) : null}

                {featuredPlaying ? (
                  <div className="relative z-10 mx-auto block aspect-video w-full overflow-hidden rounded-xl border border-[#FFFFFF52]">
                    <iframe
                      src={buildEmbedUrl(featured.youtubeVideoId)}
                      title={featured.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      referrerPolicy="strict-origin-when-cross-origin"
                      className="absolute inset-0 h-full w-full"
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setFeaturedPlaying(true)}
                    aria-label={`Play: ${featured.title}`}
                    className="group relative z-10 mx-auto block h-[200px] w-[100%] overflow-hidden rounded-xl border border-[#FFFFFF52] md:aspect-video md:h-auto md:w-full"
                  >
                    {featured.thumbnailUrl ? (
                      <img
                        src={featured.thumbnailUrl}
                        alt=""
                        className="absolute inset-0 h-full w-full object-cover transition group-hover:opacity-95"
                        loading="eager"
                        decoding="async"
                      />
                    ) : null}
                    <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <img
                        src="/resources/rplay.svg"
                        alt=""
                        width={37}
                        height={37}
                        className="h-[37px] w-[37px] shrink-0"
                        decoding="async"
                      />
                    </span>
                    {featured.durationFormatted ? (
                      <span className="absolute bottom-3 right-3 rounded bg-black/85 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-white">
                        {featured.durationFormatted}
                      </span>
                    ) : null}
                  </button>
                )}
              </div>

              <p className="text-[13px] text-[#FFFFFF85]">
                {formatMonthYear(featured.publishedAt)
                  ? `${formatMonthYear(featured.publishedAt)} • `
                  : ""}
                Latest upload
              </p>

              <h3 className="font-fira text-[18px] font-medium leading-snug text-white md:text-2xl">
                {featured.title}
              </h3>

              {featured.description ? (
                <p className="font-fira line-clamp-2 font-normal text-sm leading-relaxed text-[#CFCFCFE5]">
                  {featured.description}
                </p>
              ) : null}

              {featuredMapping && featuredDispatchSlug ? (
                <Link
                  to={`/daily-ai-updates/${featuredDispatchSlug}`}
              className="inline-flex w-full items-center justify-center rounded-lg border border-[#858585] bg-black px-5 py-2.5 font-fira text-sm font-semibold text-[#FFFFFF] transition-opacity hover:opacity-90 md:text-[0.9375rem]"
                >
                  {featuredMapping.ctaText}
                </Link>
              ) : null}
            </div>

            {/* More — right */}
            <div className="flex min-w-0 flex-col md:col-span-2">
              <h3 className="mb-4 text-sm font-medium text-[#FFFFFF85]">
                More from the channel
              </h3>
              <ul className="flex flex-col gap-0">
                {more.map((video, idx) => (
                  <li
                    key={video.id}
                    className={
                      idx < more.length - 1
                        ? "border-b border-white/10 pb-4 mb-4"
                        : ""
                    }
                  >
                    <button
                      type="button"
                      onClick={() => setActiveVideo(video)}
                      className="group flex w-full gap-3 text-left"
                      aria-label={`Open: ${video.title}`}
                    >
                      <div className="relative h-[4.75rem] w-[8.75rem] shrink-0 overflow-hidden rounded-lg border border-[#FFFFFF52] sm:h-[5rem] sm:w-36">
                        {video.thumbnailUrl ? (
                          <img
                            src={video.thumbnailUrl}
                            alt=""
                            className="h-full w-full object-cover transition group-hover:opacity-90"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : null}
                        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                          <img
                            src="/resources/rplay.svg"
                            alt=""
                            width={24}
                            height={24}
                            className="h-6 w-6 shrink-0"
                            decoding="async"
                          />
                        </span>
                        {video.durationFormatted ? (
                          <span className="absolute bottom-1 right-1 z-[1] rounded bg-black/85 px-1 py-0.5 text-[10px] font-medium tabular-nums text-white">
                            {video.durationFormatted}
                          </span>
                        ) : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-2 text-left text-sm font-medium leading-snug text-white md:text-[18px]">
                          {video.title}
                        </p>
                        <p className="mt-1 text-left text-xs text-[#CFCFCFE5] md:text-sm">
                          {video.viewCountFormatted ?? ""}
                          {formatMonthYear(video.publishedAt) ? " • " : null}
                          {formatMonthYear(video.publishedAt)}
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {status === "ready" && !featured && (
          <p className="text-[14px] text-[#FFFFFF85]">No videos yet.</p>
        )}

        {/* Mobile only — "Explore more" lives at section bottom on small
            screens; on desktop the header-row version is shown instead. */}
        <div className="mt-10 flex md:hidden">
          <a
            href="/all-resources"
            className="inline-flex w-full items-center justify-center rounded-lg border border-[#FFFFFF4D] bg-transparent px-6 py-3 text-center text-sm font-medium text-white transition hover:bg-white/5"
          >
            Explore more
          </a>
        </div>
      </div>

      {activeVideo ? (
        <VideoPlayerModal
          video={activeVideo}
          mapping={mappingIndex.get(activeVideo.youtubeVideoId) ?? null}
          onClose={() => setActiveVideo(null)}
        />
      ) : null}
    </section>
  );
}